import {
  API,
  ErrorHandling,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_APP_TERMINATING,
  executeWithRetry,
  FatalErrors,
  fetchWrapper,
  getServerRoomName,
  getUpdatedLauncherStartupOptions,
  iFrameLauncherEvent,
  type iFrameLauncherMessage,
  LauncherEventType,
  Logger,
  mocksResponse,
  type OnAppTerminatingData,
} from "@classdojo/monsterverse-utils";
import { decode, encode } from "@msgpack/msgpack";
import { Client, type Room } from "colyseus.js";

import type { PCAppEventHandler } from "./PCAppEventHandler";

const pingTimerInSeconds = 30;
const noActivityTimeoutInSeconds = 600;
const reconnectionDelayInSeconds = 1;
const reconnectionDelayVarianceInSeconds = 3;
const totalReconnectionAttempts = 5;

export class Networking {
  private client?: Client;
  private room?: Room<unknown>;
  private URL: URL;

  private receivedMessage = false;
  private reconnectAttemptTimeout?: number;
  private reconnectionAttemptsLeft = totalReconnectionAttempts;

  private timeoutTimer?: number;
  private skipNextTimeoutReset = false;

  private pingInterval?: number;
  private pingSentTime = Date.now();
  private receivedMessageCount = 0;
  private sentMessageCount = 0;

  public loadingComplete = false;

  constructor(
    private iframeEl: HTMLIFrameElement,
    pcAppEventHandler: PCAppEventHandler,
    wsUrl: URL,
    private groupId: string,
    private countryCode: string,
    private serverRegion: string,
    private desiredServerRegion: string
  ) {
    window.addEventListener("message", this.onIFrameMessage.bind(this));

    pcAppEventHandler.on(EVENT_APP_LOADING_COMPLETE, () => {
      this.loadingComplete = true;
      this.resetInactivityTimer();
    });

    pcAppEventHandler.on(EVENT_APP_TERMINATING, (data: unknown) => {
      const handler = data as OnAppTerminatingData;
      if (handler) {
        const { exitingMonsterverse, customizer } = handler;
        if (!exitingMonsterverse && customizer) {
          this.loadingComplete = false;
          if (this.timeoutTimer) {
            clearTimeout(this.timeoutTimer);
            this.timeoutTimer = undefined;
          }
        }
      }
    });

    this.URL = wsUrl;
    this.connect(false);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async validateTicketAndConnect() {
    const env = window._ENV || "local";
    if (env !== "local") {
      //Build the check ticket url
      const checkTicketUrl = new URL(this.URL.toString());
      checkTicketUrl.protocol = "https:";
      checkTicketUrl.pathname = "/checkTicket";

      let invalidTicket = false;
      try {
        //Check our ticket
        const response = await fetchWrapper("ticketCheck", checkTicketUrl.toString(), { method: "GET" });

        //Failed response
        if (!response.ok) {
          const responseText = await response.text();
          invalidTicket = responseText === "Invalid server ticket";
          throw new Error(`ticketCheck: status failure ${responseText}`);
        }

        //Update ticket with refreshed value
        try {
          const result = await response.json();
          this.URL.searchParams.set("ticket", result.ticket);
        } catch {
          throw new Error(`ticketCheck: failed to decode json response`);
        }
      } catch (e) {
        this.reconnectionAttemptsLeft--;
        if (this.reconnectionAttemptsLeft <= 0 || invalidTicket) {
          if (e instanceof Error && "type" in e && e.type === "FetchError") {
            Logger.logInfo("ticketCheck: Failed to fetch the route");
          } else if (e instanceof Error) {
            Logger.logInfo(e.message);
          } else {
            Logger.logInfo("ticketCheck: Unknown failure");
          }
          this.disconnect(invalidTicket ? FatalErrors.ServerTicketExpiredTimeout : FatalErrors.ServerReconnectTimeout);
        } else {
          this.startReconnectAttemptTimer();
        }

        //Prevent connection from happening below
        return;
      }
    }

    this.connect(true);
  }

  private connect(reconnecting: boolean) {
    this.client = new Client(this.URL.toString());

    const connectOptions: Record<string, string> = {};
    for (const [key, value] of this.URL.searchParams.entries()) {
      connectOptions[key] = value;
    }

    //Overwrite the endpoint functions to support params (ticket and logger session id)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const origHttpEndpoint = (this.client as any).getHttpEndpoint.bind(this.client);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.client as any).getHttpEndpoint = (segments = "") => {
      return `${origHttpEndpoint(segments)}?${this.URL.searchParams.toString()}`;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const origWsEndpoint = (this.client as any).buildEndpoint.bind(this.client);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.client as any).buildEndpoint = (room: any, options: any = {}) => {
      return origWsEndpoint(room, { ...options, ...connectOptions });
    };

    //Remove headers to prevent CORS requests that we don't need
    const origHttpPost = this.client.http.post.bind(this.client.http);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.client.http.post = (path: string, options: any = {}) => {
      delete options["headers"];
      return origHttpPost(path, options);
    };

    this.client
      .joinOrCreate(getServerRoomName(this.groupId), connectOptions)
      .then((room) => this.onJoin(room, reconnecting))
      .catch((reason) => {
        this.reconnectionAttemptsLeft--;
        if (this.reconnectionAttemptsLeft <= 0) {
          if (reason instanceof Error) {
            if ("code" in reason) {
              Logger.logInfo(`Colyseus error: ${reason.name} code ${reason.code} - ${reason.message}`);
            } else {
              Logger.logInfo(`Colyseus error: ${reason.name} - ${reason.message}`);
            }
          } else {
            Logger.logInfo(`Colyseus error: unknown type - ${JSON.stringify(reason)}`);
          }

          Logger.logPerfStats({
            type: "NetworkingConnection",
            site: "launcher",
            status: "disconnected",
          });

          this.disconnect(FatalErrors.WebsocketClosure);
        } else {
          this.startReconnectAttemptTimer();
        }
      });

    this.receivedMessage = false;
  }

  private onJoin(room: Room<unknown>, reconnecting: boolean) {
    this.room = room;

    this.room.onMessage("*", (_t, message) => {
      if (!this.receivedMessage) {
        Logger.logEvent("monsterverse.launcher.websocketConnected");
        this.receivedMessage = true;
      }

      this.postMessage(LauncherEventType.NetworkMessage, message);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const decoded = decode(message) as any;
      const isLauncher = "launcher" in decoded;
      if (isLauncher) {
        this.onMessage(decoded);
      }
    });

    this.room.onLeave((_code) => {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = undefined;
      }

      this.postMessage(LauncherEventType.NetworkLosing);

      Logger.logPerfStats({
        type: "NetworkingConnection",
        site: "launcher",
        status: "lost",
      });
      this.startReconnectAttemptTimer();
    });

    console.log("websocket is open, sending join");
    Logger.logEvent("monsterverse.launcher.websocketOpen");
    Logger.logPerfStats({ type: "NetworkingConnection", site: "launcher", status: "connected" });

    this.reconnectionAttemptsLeft = totalReconnectionAttempts;

    if (this.reconnectAttemptTimeout) {
      clearTimeout(this.reconnectAttemptTimeout);
      this.reconnectAttemptTimeout = undefined;
    }

    if (!reconnecting) {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer);
        this.timeoutTimer = undefined;
      }
    } else {
      this.skipNextTimeoutReset = true;
    }

    this.postMessage(LauncherEventType.NetworkConnected);

    this.sendPing();
    this.pingInterval = setInterval(this.sendPing.bind(this), pingTimerInSeconds * 1000);
  }

  private disconnect(fireDisconnectEvent?: FatalErrors) {
    //Close websocket and remove event listeners
    if (this.room) {
      this.room.removeAllListeners();
      this.room.leave(true);
    }

    //Clear out the websocket
    this.room = undefined;

    if (this.reconnectAttemptTimeout) {
      clearTimeout(this.reconnectAttemptTimeout);
      this.reconnectAttemptTimeout = undefined;
    }

    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = undefined;
    }

    if (fireDisconnectEvent) {
      this.postMessage(LauncherEventType.NetworkDisconnected);
    } else {
      this.postMessage(LauncherEventType.NetworkLosing);
    }

    //Clear received message indicator
    this.receivedMessage = false;

    if (fireDisconnectEvent) {
      ErrorHandling.instance.triggerFatalError(fireDisconnectEvent);
    }
  }

  private sendPing() {
    if (this.room === undefined) {
      return;
    }

    //Send ping to keep connection alive
    this.room.sendBytes("all", encode({ type: "Ping", launcher: true }));
    this.sentMessageCount++;
    this.pingSentTime = Date.now();
  }

  private startReconnectAttemptTimer() {
    if (this.reconnectAttemptTimeout !== undefined) {
      return;
    }

    this.reconnectAttemptTimeout = setTimeout(() => {
      this.reconnectAttemptTimeout = undefined;
      this.validateTicketAndConnect();
    }, (reconnectionDelayInSeconds + Math.random() * reconnectionDelayVarianceInSeconds) * 1000);
  }

  private onMessage(message: { type: string; data?: string }) {
    switch (message.type) {
      case "Ping":
        this.receivedMessageCount++;
        Logger.logPerfStats({
          type: "Networking",
          site: "launcher",
          sentMessages: this.sentMessageCount,
          receivedMessages: this.receivedMessageCount,
          ping: Date.now() - this.pingSentTime,
          countryCode: this.countryCode,
          serverRegion: this.serverRegion,
          desiredServerRegion: this.desiredServerRegion,
        });
        this.sentMessageCount = 0;
        this.receivedMessageCount = 0;
        break;
      case "ServerShuttingDown":
        console.log("Got server shutting down");
        this.disconnect(FatalErrors.ServerShuttingDown);
        break;
      case "RemovePlayer":
        console.log("Got player removal");
        this.disconnect(FatalErrors.RemovePlayer);
        break;
    }

    this.receivedMessageCount++;
  }

  private postMessage(type: LauncherEventType, data: string | ArrayBuffer | undefined = undefined) {
    this.iframeEl.contentWindow?.postMessage({ iFrameLauncherEvent, type, data }, "*");
  }

  private onIFrameMessage(ev: MessageEvent<iFrameLauncherMessage>) {
    if (ev?.data?.iFrameLauncherEvent !== iFrameLauncherEvent) {
      return;
    }

    switch (ev.data.type) {
      case LauncherEventType.NetworkMessage:
        if (ev.data.data) {
          this.onNetworkMessage(ev.data.data as Uint8Array);
        }
        break;
      case LauncherEventType.ChangeIsland:
        if (ev.data.data) {
          const { islandType, islandId } = JSON.parse(ev.data.data as string);
          console.log("Got request to change island with", islandType, islandId);
          this.onChangeIsland(islandType, islandId);
        }
        break;
      case LauncherEventType.Reload:
        this.iframeEl.remove();
        location.reload();
        break;
      case LauncherEventType.IframeLoaded:
        if (this.room) {
          this.postMessage(LauncherEventType.NetworkConnected);
        }
        break;
    }
  }

  private onNetworkMessage(data: Uint8Array) {
    if (this.room === undefined) {
      return;
    }

    this.room.sendBytes("all", data);
    this.sentMessageCount++;
    this.resetInactivityTimer();
  }

  private resetInactivityTimer() {
    const env = window._ENV || "local";
    if (env !== "local" && this.loadingComplete && !this.skipNextTimeoutReset) {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer);
        this.timeoutTimer = undefined;
      }

      this.timeoutTimer = setTimeout(() => {
        console.log("Got inactivity timeout");
        this.disconnect(FatalErrors.ServerIdleTimeout);
      }, noActivityTimeoutInSeconds * 1000);
    }
    this.skipNextTimeoutReset = false;
  }

  private onChangeIsland(islandType: "class" | "home" | "community" | "personal", islandId: string) {
    const url = new URL(location.href);

    if (mocksResponse()) {
      url.search = getUpdatedLauncherStartupOptions(islandId);
      location.replace(url);
      return;
    }

    if (islandType !== "class") {
      url.search = getUpdatedLauncherStartupOptions(islandId);
      location.replace(url);
      return;
    }

    // call API to get updated island url for target classId
    // (startupParams might be different from current island)

    // Remove islandId type prefix
    const classId = islandId.replace(/^[a-z]+-/, "");
    executeWithRetry(
      () =>
        API.call<{ config: { island: { targetUrl: string; startupParams?: Record<string, string> } } }>({
          method: "GET",
          path: `/api/monsterverseConfig?classId=${classId}`,
        }),
      2
    )
      .then((response) => {
        url.search = getUpdatedLauncherStartupOptions(islandId, response.config.island.startupParams || {});
        //Do a replace to not keep history (Android navigates "back" to close Monsterverse)
        location.replace(url);
      })
      .catch(() => {
        // fallback to try to open the island with same startup params as current island
        url.search = getUpdatedLauncherStartupOptions(islandId);
        location.replace(url);
      });
  }
}
